import { Check, MapPin, Navigation } from 'lucide-react';
import { useState } from 'react';
import type { CalendarItem, NowContext } from '@tempo/core';
import { MODE_LABEL, dayName, duration, time } from '../../lib/format';
import { store } from '../../lib/device';
import { ModeIcon } from '../kinds';

/** Circular countdown. */
export function Ring({ fraction, size = 180, children }: { fraction: number; size?: number; children?: React.ReactNode }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const f = Math.min(1, Math.max(0, fraction));
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
        <circle cx="50" cy="50" r={r} className="ring-track" />
        <circle cx="50" cy="50" r={r} className="ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - f)} transform="rotate(-90 50 50)" />
      </svg>
      <div className="ring-label">{children}</div>
    </div>
  );
}

export const mmss = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};

/** "until 11:00 · 34m left" */
export function untilLine(item: CalendarItem | null, now: number) {
  if (!item) return null;
  return `until ${time(item.end)} · ${duration(item.end - now)} left`;
}

/** The next thing, with when to leave for it. */
export function NextCard({ n, now, label = 'Up next' }: { n: NowContext; now: number; label?: string }) {
  const next = n.nextUp.item && n.nextUp.item.start > now ? n.nextUp.item : n.next;
  const leaveAt = n.nextUp.journey?.leaveAt ?? n.nextUp.live?.leaveAt ?? n.nextUp.leaveAt;
  if (!next) return <p className="subtle">Nothing else today.</p>;
  const leaveIn = leaveAt ? leaveAt - now : null;
  return (
    <div className="stack tight">
      <div className="a-label">{label}</div>
      <div className="a-big">{next.title}</div>
      <div className="subtle">
        {dayName(next.start) === 'Today' ? '' : `${dayName(next.start)} `}
        {time(next.start)}
        {next.location ? (
          <>
            {' '}
            · <MapPin size={13} style={{ verticalAlign: -2 }} /> {next.location}
          </>
        ) : null}
      </div>
      {leaveAt && next.id === n.nextUp.item?.id && (
        <div className="a-leave">
          <ModeIcon mode={n.nextUp.mode} size={16} />
          {leaveIn! <= 0 ? 'Leave now' : `Leave at ${time(leaveAt)} · in ${duration(leaveIn!)}`}
          {n.nextUp.travelMin ? <span className="faint"> · {n.nextUp.travelMin} min {MODE_LABEL[n.nextUp.mode ?? ''] ?? ''}</span> : null}
        </div>
      )}
      {n.nextUp.directionsUrl && next.id === n.nextUp.item?.id && (
        <a className="btn" style={{ alignSelf: 'flex-start' }} href={n.nextUp.directionsUrl} target="_blank" rel="noreferrer">
          <Navigation size={16} /> Directions
        </a>
      )}
    </div>
  );
}

export function TomorrowLine({ n }: { n: NowContext }) {
  const t = n.tomorrowFirst;
  if (!t) return null;
  return (
    <p className="subtle">
      {dayName(t.start)} starts with <b>{t.title}</b> at {time(t.start)}
      {t.location ? ` · ${t.location}` : ''}
    </p>
  );
}

/** Tick-off list remembered on this device. */
export function CheckList({ id, items }: { id: string; items: string[] }) {
  const key = `tempo.check.${id}`;
  const [done, setDone] = useState<string[]>(() => store.get<string[]>(key, []));
  const toggle = (s: string) => {
    const next = done.includes(s) ? done.filter((x) => x !== s) : [...done, s];
    setDone(next);
    store.set(key, next);
  };
  return (
    <div className="checklist">
      {items.map((s) => (
        <button key={s} className={`check ${done.includes(s) ? 'on' : ''}`} onClick={() => toggle(s)}>
          <span className="box">{done.includes(s) && <Check size={14} />}</span>
          <span>{s}</span>
        </button>
      ))}
    </div>
  );
}
